import React from "react";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";

import eventRefs from "../../../assets/eventRef";
import LoggingEventLogs from "../../logging/eventLogs";
import { EVENT_ALERT_KIND } from "../../../utils/eventLogKinds";
import {
  buildAlertCreatePath,
  buildAlertDetailsPath,
  buildAlertEditPath,
  getEventDescription,
  getEventRefByCode,
} from "../../../utils/deviceAlertUtils";

import "./devicePageAlerts.css";

const EVENT_REF_MAP = new Map(eventRefs.map((ref) => [String(ref.code), ref]));
const ALERTS_PER_PAGE = 10;
const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All alerts" },
  { value: "triggered", label: "Triggered" },
  { value: "clear", label: "Monitoring / Clear" },
];
const SORT_OPTIONS = [
  { value: "active_first", label: "Active first" },
  { value: "last_triggered_desc", label: "Recently triggered" },
  { value: "last_triggered_asc", label: "Oldest triggered" },
  { value: "name_asc", label: "Alert name A-Z" },
  { value: "name_desc", label: "Alert name Z-A" },
];

const compareStrings = (left, right) =>
  String(left ?? "").localeCompare(String(right ?? ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });

const getActionCount = (actions) => {
  if (Array.isArray(actions)) {
    return actions.length;
  }

  if (typeof actions === "string") {
    try {
      const parsed = JSON.parse(actions);
      if (Array.isArray(parsed)) {
        return parsed.length;
      }
    } catch (_error) {
      return 0;
    }
  }

  return 0;
};

const toDateFromEventExecution = (event) => {
  const rawValue = event?.timeExecutedInEpoch;
  if (!rawValue) {
    return null;
  }

  const nextDate = new Date(Number(rawValue) * 1000);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
};

const toDateFromLastChange = (event) => {
  const rawValue = event?.lastChange;
  if (!rawValue) {
    return null;
  }

  const nextDate = new Date(rawValue);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
};

const getLastSyncDate = (device, events = []) => {
  const eventDates = events
    .map((event) => toDateFromLastChange(event))
    .filter(Boolean)
    .sort((left, right) => right.getTime() - left.getTime());

  if (eventDates.length > 0) {
    return eventDates[0];
  }

  const deviceDate = device?.lastHeard ? new Date(device.lastHeard) : null;
  return deviceDate && !Number.isNaN(deviceDate.getTime()) ? deviceDate : null;
};

const getAlertTypeOptions = (events = []) =>
  Array.from(
    new Set(
      events
        .map((event) => String(event?.code ?? ""))
        .filter(Boolean),
    ),
  )
    .map((value) => ({
      value,
      label: EVENT_REF_MAP.get(value)?.name || `Event ${value}`,
    }))
    .sort((left, right) => compareStrings(left.label, right.label));

const getAlertCategory = (eventRefName = "") => {
  const normalized = String(eventRefName).toLowerCase();

  if (normalized.startsWith("inlet") || normalized.startsWith("bank")) {
    return "Power";
  }

  if (normalized.startsWith("outlet")) {
    return "Outlet";
  }

  if (
    normalized.includes("temperature") ||
    normalized.includes("humidity") ||
    normalized.includes("dewpoint")
  ) {
    return "Environmental";
  }

  if (
    normalized.includes("sensor") ||
    normalized.includes("digital") ||
    normalized.includes("switch")
  ) {
    return "Sensor / IO";
  }

  if (
    normalized.includes("network") ||
    normalized.includes("ping") ||
    normalized.includes("ethernet")
  ) {
    return "Network";
  }

  if (normalized.includes("schedule")) {
    return "Scheduled";
  }

  if (normalized.includes("ats")) {
    return "ATS";
  }

  return "Alert";
};

const getAlertDescription = (event) => {
  const description = getEventDescription(event);
  return description || "No description available for this alert.";
};

const buildSearchText = (event) => {
  const eventRefName = getEventRefByCode(event?.code)?.name || "";
  const parameters = Array.isArray(event?.params) ? event.params.join(" ") : "";

  return [
    event?.name,
    eventRefName,
    event?.enclosureSerialNumber,
    event?.deviceName,
    parameters,
    getAlertDescription(event),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const getTimingCopy = (event) => {
  const lastTriggered = toDateFromEventExecution(event);

  if (event?.triggered) {
    return {
      prominent: lastTriggered
        ? `Active ${formatDistanceToNow(lastTriggered)}`
        : "Triggered now",
      secondary: lastTriggered ? format(lastTriggered, "MMM d, h:mm a") : "Awaiting timestamp",
    };
  }

  if (!lastTriggered) {
    return {
      prominent: "Never triggered",
      secondary: "No trigger history yet",
    };
  }

  return {
    prominent: `Last trig ${formatDistanceToNow(lastTriggered)}`,
    secondary: format(lastTriggered, "MMM d, h:mm a"),
  };
};

const getMetaCopy = (event) => {
  const eventRefName = getEventRefByCode(event?.code)?.name || `Event ${event?.code ?? "—"}`;
  const category = getAlertCategory(eventRefName);
  const actionCount = getActionCount(event?.actions);
  const actionLabel =
    actionCount > 0
      ? `${actionCount} action${actionCount === 1 ? "" : "s"} attached`
      : "No actions attached";

  return `${eventRefName} • ${category} • ${actionLabel}`;
};

const getSummaryCounts = (events = []) => {
  const totalAlerts = events.length;
  const activeAlerts = events.filter((event) => Boolean(event?.triggered)).length;

  return {
    totalAlerts,
    activeAlerts,
    clearAlerts: Math.max(0, totalAlerts - activeAlerts),
  };
};

const sortAlerts = (events, sortFilter) => {
  const copy = [...events];

  copy.sort((left, right) => {
    if (sortFilter === "name_asc") {
      return compareStrings(left?.name, right?.name);
    }

    if (sortFilter === "name_desc") {
      return compareStrings(right?.name, left?.name);
    }

    const leftTriggeredAt = toDateFromEventExecution(left)?.getTime() || 0;
    const rightTriggeredAt = toDateFromEventExecution(right)?.getTime() || 0;

    if (sortFilter === "last_triggered_asc") {
      if (leftTriggeredAt !== rightTriggeredAt) {
        return leftTriggeredAt - rightTriggeredAt;
      }

      return compareStrings(left?.name, right?.name);
    }

    if (sortFilter === "last_triggered_desc") {
      if (leftTriggeredAt !== rightTriggeredAt) {
        return rightTriggeredAt - leftTriggeredAt;
      }

      return compareStrings(left?.name, right?.name);
    }

    if (sortFilter === "active_first") {
      if (leftTriggeredAt !== rightTriggeredAt) {
        return rightTriggeredAt - leftTriggeredAt;
      }

      const leftUpdatedAt = toDateFromLastChange(left)?.getTime() || 0;
      const rightUpdatedAt = toDateFromLastChange(right)?.getTime() || 0;

      if (leftUpdatedAt !== rightUpdatedAt) {
        return rightUpdatedAt - leftUpdatedAt;
      }
    }

    return compareStrings(left?.name, right?.name);
  });

  return copy;
};

const SUMMARY_TILE_COLORS = {
  neutral: "#95a5a6",
  danger: "#e74c3c",
  success: "#2ecc71",
  warning: "#f6c343",
};

const AlertStatusPill = ({ children, tone }) => (
  <span
    className={[
      "device-alerts-center__pill",
      tone ? `device-alerts-center__pill--${tone}` : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    {children}
  </span>
);

const SummaryTile = ({ label, value, color, compactValue }) => (
  <div className="device-alerts-center__summary-tile">
    <div
      className={[
        "device-alerts-center__summary-tile-value",
        compactValue ? "device-alerts-center__summary-tile-value--compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value}
    </div>
    <div className="device-alerts-center__summary-tile-label">{label}</div>
    <div
      className="device-alerts-center__summary-tile-accent"
      style={{ background: color }}
    />
  </div>
);

const EmptyState = ({ hasAlerts, createPath, canEditEvents }) => (
  <section className="device-alerts-center__empty-state">
    <div className="device-alerts-center__empty-title">
      {hasAlerts ? "No alerts match these filters." : "No alerts configured yet."}
    </div>
    <p className="device-alerts-center__empty-copy">
      {hasAlerts
        ? "Adjust the search, type, or status filters to bring alerts back into view."
        : "Create an alert to start monitoring this device and capture alert history below."}
    </p>
    <div className="device-alerts-center__empty-actions">
      {canEditEvents ? (
        <Link to={createPath} className="button">
          Create New Alert Event
        </Link>
      ) : null}
      <Link to="/alerts-documentation" className="button secondary">
        Learn More about Alerts
      </Link>
    </div>
  </section>
);

const AlertRowSkeleton = () => (
  <div className="device-alerts-center__row device-alerts-center__row--loading">
    <div className="device-alerts-center__skeleton-pill skeleton-blank" />
    <div className="device-alerts-center__row-main">
      <div className="device-alerts-center__skeleton-line skeleton-blank" />
      <div className="device-alerts-center__skeleton-line device-alerts-center__skeleton-line--short skeleton-blank" />
    </div>
    <div className="device-alerts-center__row-meta">
      <div className="device-alerts-center__skeleton-line device-alerts-center__skeleton-line--short skeleton-blank" />
    </div>
    <div className="device-alerts-center__row-time">
      <div className="device-alerts-center__skeleton-line device-alerts-center__skeleton-line--tiny skeleton-blank" />
    </div>
  </div>
);

class DevicePageAlerts extends React.Component {
  state = {
    searchText: "",
    statusFilter: "all",
    eventTypeFilter: "",
    sortFilter: "active_first",
    page: 1,
  };

  setFilterState = (nextState) => {
    this.setState({
      ...nextState,
      page: 1,
    });
  };

  renderAlertRow = (event) => {
    const serial = String(
      event?.enclosureSerialNumber ?? this.props.match?.params?.enclosureSerialNumber ?? "",
    );
    const eventRef = getEventRefByCode(event?.code);
    const detailPath = buildAlertDetailsPath(serial, event?.id);
    const editPath = buildAlertEditPath(serial, event?.id);
    const syncDate = toDateFromLastChange(event);
    const timing = getTimingCopy(event);
    const isOffline = this.props.deviceOffline || event?.connectionState === "Disconnected";

    return (
      <article
        key={`${serial}:${event?.id ?? event?.name ?? "unknown"}`}
        className={[
          "device-alerts-center__row",
          event?.triggered ? "device-alerts-center__row--triggered" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="device-alerts-center__row-status">
          <AlertStatusPill tone={event?.triggered ? "danger" : "success"}>
            {event?.triggered ? "Triggered" : "Clear"}
          </AlertStatusPill>
          {event?.stale ? <AlertStatusPill tone="warning">Stale</AlertStatusPill> : null}
          {isOffline ? (
            <AlertStatusPill tone="warning">Device offline</AlertStatusPill>
          ) : null}
        </div>

        <div className="device-alerts-center__row-main">
          <Link to={detailPath} className="device-alerts-center__row-title">
            {event?.name || eventRef?.name || "Unnamed alert"}
          </Link>
          <div className="device-alerts-center__row-summary">{getAlertDescription(event)}</div>
          {(event?.stale || isOffline) && (
            <div className="device-alerts-center__row-note">
              {isOffline && this.props.offlineReason
                ? this.props.offlineReason
                : `Showing last known device state${
                    syncDate
                      ? ` from ${formatDistanceToNow(syncDate, {
                          addSuffix: true,
                        })}`
                      : ""
                  }.`}
            </div>
          )}
        </div>

        <div className="device-alerts-center__row-meta">
          <div className="device-alerts-center__row-meta-label">Alert context</div>
          <div className="device-alerts-center__row-meta-copy">{getMetaCopy(event)}</div>
        </div>

        <div className="device-alerts-center__row-time">
          <div className="device-alerts-center__row-time-prominent">{timing.prominent}</div>
          <div className="device-alerts-center__row-time-secondary">{timing.secondary}</div>
        </div>

        <div className="device-alerts-center__row-actions">
          <Link to={detailPath} className="device-alerts-center__row-action">
            View details
          </Link>
          {this.props.canEditEvents ? (
            <>
              <span className="device-alerts-center__row-actions-divider">•</span>
              <Link
                to={editPath}
                className="device-alerts-center__row-action device-alerts-center__row-action--muted"
              >
                Edit alert
              </Link>
            </>
          ) : null}
        </div>
      </article>
    );
  };

  render() {
    const { device, events = [], loading, match, location, canEditEvents } = this.props;
    const serial = String(match?.params?.enclosureSerialNumber ?? device?.enclosureSerialNumber ?? "");
    const createPath = buildAlertCreatePath(serial);
    const historyPath = `/devices/connected/${serial}/event-logs?kind=${encodeURIComponent(
      EVENT_ALERT_KIND,
    )}`;
    const lastSyncDate = getLastSyncDate(device, events);
    const summary = getSummaryCounts(events);
    const hasStaleAlerts = events.some((event) => Boolean(event?.stale));

    const normalizedSearch = this.state.searchText.trim().toLowerCase();
    const baseFilteredAlerts = events.filter((event) => {
      if (this.state.eventTypeFilter && String(event?.code) !== this.state.eventTypeFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return buildSearchText(event).includes(normalizedSearch);
    });

    const filteredCounts = getSummaryCounts(baseFilteredAlerts);
    const statusFilteredAlerts = baseFilteredAlerts.filter((event) => {
      if (this.state.statusFilter === "triggered") {
        return Boolean(event?.triggered);
      }

      if (this.state.statusFilter === "clear") {
        return !event?.triggered;
      }

      return true;
    });

    const triggeredAlerts = sortAlerts(
      statusFilteredAlerts.filter((event) => Boolean(event?.triggered)),
      this.state.sortFilter,
    );
    const clearAlerts = sortAlerts(
      statusFilteredAlerts.filter((event) => !event?.triggered),
      this.state.sortFilter,
    );
    const orderedAlerts =
      this.state.statusFilter === "triggered"
        ? triggeredAlerts
        : this.state.statusFilter === "clear"
        ? clearAlerts
        : [...triggeredAlerts, ...clearAlerts];

    const totalPages = Math.max(1, Math.ceil(orderedAlerts.length / ALERTS_PER_PAGE));
    const currentPage = Math.min(this.state.page, totalPages);
    const pageStart = (currentPage - 1) * ALERTS_PER_PAGE;
    const paginatedAlerts = orderedAlerts.slice(pageStart, pageStart + ALERTS_PER_PAGE);
    const paginatedTriggeredAlerts = paginatedAlerts.filter((event) => Boolean(event?.triggered));
    const paginatedClearAlerts = paginatedAlerts.filter((event) => !event?.triggered);
    const eventTypeOptions = getAlertTypeOptions(events);
    const showingStart = orderedAlerts.length === 0 ? 0 : pageStart + 1;
    const showingEnd = pageStart + paginatedAlerts.length;
    const summaryText =
      orderedAlerts.length === 0
        ? `Showing 0 of ${orderedAlerts.length} alerts`
        : `Showing ${showingStart}-${showingEnd} of ${orderedAlerts.length} alerts`;
    const summaryTiles = [
      {
        key: "deviceStatus",
        label: "Device Status",
        value: this.props.deviceOffline ? "Offline" : "Online",
        color: this.props.deviceOffline
          ? SUMMARY_TILE_COLORS.warning
          : SUMMARY_TILE_COLORS.success,
      },
      {
        key: "allAlerts",
        label: "All Alerts",
        value: summary.totalAlerts,
        color: SUMMARY_TILE_COLORS.neutral,
      },
      {
        key: "triggeredNow",
        label: "Triggered Now",
        value: summary.activeAlerts,
        color: SUMMARY_TILE_COLORS.danger,
      },
      {
        key: "monitoring",
        label: "Monitoring",
        value: summary.clearAlerts,
        color: SUMMARY_TILE_COLORS.neutral,
      },
      {
        key: "lastSynced",
        label: "Last Synced",
        value: lastSyncDate
          ? formatDistanceToNow(lastSyncDate).replace(/^about /, "")
          : "Unavailable",
        color: SUMMARY_TILE_COLORS.neutral,
        compactValue: true,
      },
    ];

    return (
      <div className="device-alerts-center">
        <section className="device-alerts-center__summary-grid">
          {summaryTiles.map((tile) => (
            <SummaryTile
              key={tile.key}
              label={tile.label}
              value={tile.value}
              color={tile.color}
              compactValue={tile.compactValue}
            />
          ))}
        </section>

        <section className="device-alerts-center__intro">
          <div className="device-alerts-center__intro-copy">
            <p className="device-alerts-center__intro-description">
              {canEditEvents
                ? "Create alert events for this device, review current states, and monitor recent history from one page."
                : "Review alert states and monitor recent history for this device."}{" "}
              <Link to="/alerts-documentation" className="device-alerts-center__inline-link">
                Learn more about alerts
              </Link>
              .
            </p>
            {(this.props.deviceOffline || hasStaleAlerts) && (
              <p className="device-alerts-center__intro-note">
                {this.props.deviceOffline
                  ? this.props.offlineReason || "Device is offline. Showing last known alert state."
                  : "Some alert states are stale. Review timestamps before taking action."}
              </p>
            )}
          </div>

          {canEditEvents ? (
            <div className="device-alerts-center__intro-actions">
              <Link to={createPath} className="button" style={{ marginBottom: 0 }}>
                Create New Alert
              </Link>
            </div>
          ) : null}
        </section>

        <section className="device-alerts-center__toolbar">
          <div className="device-alerts-center__status-filters">
            {STATUS_FILTER_OPTIONS.map((option) => {
              const count =
                option.value === "triggered"
                  ? filteredCounts.activeAlerts
                  : option.value === "clear"
                  ? filteredCounts.clearAlerts
                  : filteredCounts.totalAlerts;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={[
                    "device-alerts-center__status-filter",
                    this.state.statusFilter === option.value
                      ? "device-alerts-center__status-filter--active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-pressed={this.state.statusFilter === option.value}
                  onClick={() => this.setFilterState({ statusFilter: option.value })}
                >
                  <span>{option.label}</span>
                  <span className="device-alerts-center__status-filter-count">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="device-alerts-center__control-row">
            <label className="device-alerts-center__control device-alerts-center__control--search">
              <span className="device-alerts-center__control-label">Search alerts</span>
              <input
                type="text"
                value={this.state.searchText}
                onChange={(event) =>
                  this.setFilterState({ searchText: event.target.value })
                }
                placeholder="Search alert names, types, or parameters"
              />
            </label>

            <label className="device-alerts-center__control">
              <span className="device-alerts-center__control-label">Alert type</span>
              <select
                value={this.state.eventTypeFilter}
                onChange={(event) =>
                  this.setFilterState({ eventTypeFilter: event.target.value })
                }
              >
                <option value="">All alert types</option>
                {eventTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="device-alerts-center__control">
              <span className="device-alerts-center__control-label">Sort</span>
              <select
                value={this.state.sortFilter}
                onChange={(event) =>
                  this.setFilterState({ sortFilter: event.target.value })
                }
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="device-alerts-center__toolbar-actions">
              {(this.state.searchText || this.state.eventTypeFilter || this.state.statusFilter !== "all") && (
                <button
                  type="button"
                  className="button secondary"
                  onClick={() =>
                    this.setState({
                      searchText: "",
                      statusFilter: "all",
                      eventTypeFilter: "",
                      sortFilter: "active_first",
                      page: 1,
                    })
                  }
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>

          <div className="device-alerts-center__summary">{summaryText}</div>
        </section>

        <section className="device-alerts-center__list">
          {loading ? (
            <div className="device-alerts-center__section">
              <div className="device-alerts-center__section-heading">Alerts</div>
              <div className="device-alerts-center__rows">
                {[0, 1, 2, 3].map((index) => (
                  <AlertRowSkeleton key={index} />
                ))}
              </div>
            </div>
          ) : orderedAlerts.length === 0 ? (
            <EmptyState
              hasAlerts={events.length > 0}
              createPath={createPath}
              canEditEvents={canEditEvents}
            />
          ) : (
            <>
              {paginatedTriggeredAlerts.length > 0 && (
                <div className="device-alerts-center__section">
                  <div className="device-alerts-center__section-heading">Triggered Now</div>
                  <div className="device-alerts-center__rows">
                    {paginatedTriggeredAlerts.map(this.renderAlertRow)}
                  </div>
                </div>
              )}

              {paginatedClearAlerts.length > 0 && (
                <div className="device-alerts-center__section">
                  <div className="device-alerts-center__section-heading">
                    Monitoring / Clear
                  </div>
                  <div className="device-alerts-center__rows">
                    {paginatedClearAlerts.map(this.renderAlertRow)}
                  </div>
                </div>
              )}
            </>
          )}

          {totalPages > 1 && !loading && orderedAlerts.length > 0 && (
            <div className="device-alerts-center__pagination">
              <ReactPaginate
                previousLabel="Previous"
                nextLabel="Next"
                breakLabel="..."
                forcePage={currentPage - 1}
                pageCount={totalPages}
                onPageChange={({ selected }) =>
                  this.setState({ page: selected + 1 })
                }
                marginPagesDisplayed={1}
                pageRangeDisplayed={5}
                containerClassName="react-paginate"
                activeClassName="selected"
              />
            </div>
          )}
        </section>

        <section className="device-alerts-center__history">
          <div className="device-alerts-center__history-header">
            <div>
              <h3 className="device-alerts-center__history-title">Alert History</h3>
              <div className="device-alerts-center__history-copy">
                Recent alert activity for this device. Use the timeframe below when you
                want to focus on a specific period.
              </div>
            </div>
          </div>

          <LoggingEventLogs
            embedded
            hideTitle
            hideSearch
            hideEventTypeFilter
            hideExport
            hideFilterChips
            hideClearFilters
            hideOperationFilterButton
            hideAlertsOnlyToggle
            hideSummary
            hidePagination
            descriptionClampLines={1}
            compactTable
            tableClassName="device-alerts-center__history-table"
            device={device}
            match={{ params: { enclosureSerialNumber: serial } }}
            location={location || { search: "" }}
            queryParams={{
              enclosure_serial_number: serial,
              kind: EVENT_ALERT_KIND,
            }}
            initialLimit={5}
          />

          <div className="device-alerts-center__history-footer">
            <Link to={historyPath} className="device-alerts-center__history-link">
              See all alert history
            </Link>
          </div>
        </section>
      </div>
    );
  }
}

export default DevicePageAlerts;
